import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Video, Calendar, Clock, User, AlertCircle, Play, LogIn, Mic, MicOff, Camera, CameraOff, Mail } from 'lucide-react';
import { api, tokenStorage, ws } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useMeeting } from '../context/MeetingContext';
import type { MeetingJoinTokenResponse } from '@sahaj/types';

export const PublicMeetingJoinPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { joinMeetingRoom } = useMeeting();

  const [tokenInfo, setTokenInfo] = useState<MeetingJoinTokenResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Guest inputs
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [isJoiningGuest, setIsJoiningGuest] = useState(false);

  // Pre-join media state
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCameraOn, setIsCameraOn] = useState(true);

  useEffect(() => {
    if (!token) {
      setError('No meeting token provided');
      setIsLoading(false);
      return;
    }

    api.meetings
      .validateJoinToken(token)
      .then((info) => {
        setTokenInfo(info);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'Invalid or expired meeting link');
        setIsLoading(false);
      });
  }, [token]);

  const handleJoinAsUser = () => {
    if (!isAuthenticated) {
      navigate(`/login?returnUrl=/meet/${token}`);
      return;
    }

    if (tokenInfo) {
      navigate(`/app?joinMeetingId=${tokenInfo.meetingId}`);
    }
  };

  const handleGuestJoinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !tokenInfo) return;
    const cleanName = guestName.trim();
    if (!cleanName) {
      setError('Please enter your display name');
      return;
    }

    setIsJoiningGuest(true);
    setError(null);

    try {
      const payload: any = { displayName: cleanName };
      if (guestEmail.trim()) {
        payload.email = guestEmail.trim();
      }

      const response = await api.meetings.joinAsGuest(token, payload);

      // Set temporary guest token in session storage
      await tokenStorage.setAccessToken(response.accessToken);
      if (response.guestId) {
        localStorage.setItem('sahaj_auth_guest_id', response.guestId);
      }

      // Re-establish STOMP connection using Guest JWT token
      ws.disconnect();
      await ws.connect();

      if (ws.getState() !== 'CONNECTED') {
        throw new Error('Failed to establish real-time signaling connection as guest');
      }

      // Join WebRTC room with guest identity
      await joinMeetingRoom(response.workspaceId, response.meetingId, response.guestId);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to join meeting as guest');
    } finally {
      setIsJoiningGuest(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-lg bg-card border border-border rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border bg-gradient-to-r from-primary/10 via-card to-card text-center space-y-2">
          <div className="p-3 bg-primary/20 text-primary rounded-xl w-fit mx-auto shadow-sm">
            <Video className="w-8 h-8" />
          </div>
          <h1 className="text-xl font-bold text-foreground">Sahaj Connect Meeting</h1>
          <p className="text-xs text-muted-foreground">Secure real-time audio/video collaboration</p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {isLoading ? (
            <div className="py-8 text-center text-xs text-muted-foreground space-y-2">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              <p>Validating meeting link...</p>
            </div>
          ) : error ? (
            <div className="py-6 text-center space-y-3">
              <div className="p-3 bg-destructive/15 text-destructive rounded-full w-fit mx-auto">
                <AlertCircle className="w-6 h-6" />
              </div>
              <h2 className="text-sm font-bold text-foreground">Link Unavailable</h2>
              <p className="text-xs text-muted-foreground">{error}</p>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="px-4 py-2 text-xs font-medium bg-muted hover:bg-muted/80 text-foreground rounded-lg transition"
              >
                Return Home
              </button>
            </div>
          ) : tokenInfo ? (
            <div className="space-y-5">
              <div className="space-y-2 text-center">
                <span className={`px-2.5 py-0.5 text-[10px] font-bold rounded-full inline-block ${
                  tokenInfo.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                  tokenInfo.status === 'SCHEDULED' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {tokenInfo.status}
                </span>
                <h2 className="text-lg font-bold text-foreground">{tokenInfo.title}</h2>
                {tokenInfo.description && (
                  <p className="text-xs text-muted-foreground">{tokenInfo.description}</p>
                )}
              </div>

              {/* Meeting Info Card */}
              <div className="p-4 bg-muted/20 border border-border rounded-xl space-y-2 text-xs">
                <div className="flex items-center justify-between text-muted-foreground">
                  <span className="flex items-center gap-1.5 font-medium">
                    <User className="w-3.5 h-3.5 text-primary" /> Host
                  </span>
                  <span className="text-foreground font-semibold">{tokenInfo.hostName}</span>
                </div>

                <div className="flex items-center justify-between text-muted-foreground">
                  <span className="flex items-center gap-1.5 font-medium">
                    <Calendar className="w-3.5 h-3.5 text-primary" /> Start
                  </span>
                  <span className="text-foreground font-medium">
                    {new Date(tokenInfo.startTime).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                </div>

                <div className="flex items-center justify-between text-muted-foreground">
                  <span className="flex items-center gap-1.5 font-medium">
                    <Clock className="w-3.5 h-3.5 text-primary" /> Timezone
                  </span>
                  <span className="text-foreground font-medium">{tokenInfo.timezone}</span>
                </div>
              </div>

              {tokenInfo.isJoinable ? (
                <div className="space-y-5">
                  {/* Pre-join Media Controls */}
                  <div className="flex items-center justify-center gap-3 py-2 bg-muted/30 border border-border rounded-xl">
                    <button
                      type="button"
                      onClick={() => setIsMicOn(!isMicOn)}
                      className={`p-2.5 rounded-full transition ${
                        isMicOn ? 'bg-primary/20 text-primary' : 'bg-destructive/20 text-destructive'
                      }`}
                      title={isMicOn ? 'Mute Microphone' : 'Unmute Microphone'}
                    >
                      {isMicOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
                    </button>

                    <button
                      type="button"
                      onClick={() => setIsCameraOn(!isCameraOn)}
                      className={`p-2.5 rounded-full transition ${
                        isCameraOn ? 'bg-primary/20 text-primary' : 'bg-destructive/20 text-destructive'
                      }`}
                      title={isCameraOn ? 'Turn Camera Off' : 'Turn Camera On'}
                    >
                      {isCameraOn ? <Camera className="w-4 h-4" /> : <CameraOff className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Guest Join Form */}
                  <form onSubmit={handleGuestJoinSubmit} className="space-y-4 pt-2 border-t border-border">
                    <h3 className="text-xs font-bold text-foreground">Join as External Guest</h3>
                    <div>
                      <label className="block text-[11px] font-semibold text-muted-foreground mb-1">
                        Your Display Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        placeholder="e.g. Jane Doe (External Partner)"
                        className="w-full px-3 py-2 text-xs bg-muted/40 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                        <Mail className="w-3 h-3 text-primary" /> Email Address (Optional)
                      </label>
                      <input
                        type="email"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        placeholder="jane@external-company.com"
                        className="w-full px-3 py-2 text-xs bg-muted/40 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isJoiningGuest}
                      className="w-full py-2.5 text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 rounded-xl transition shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <Play className="w-4 h-4 fill-current" />
                      {isJoiningGuest ? 'Joining Meeting...' : 'Join Meeting as Guest'}
                    </button>
                  </form>

                  {/* Logged in member option */}
                  <div className="pt-3 border-t border-border text-center">
                    <button
                      type="button"
                      onClick={handleJoinAsUser}
                      className="text-xs text-primary hover:underline flex items-center justify-center gap-1.5 mx-auto font-medium"
                    >
                      {isAuthenticated ? (
                        <>
                          <Video className="w-3.5 h-3.5" /> Sign in as Workspace Member to Join
                        </>
                      ) : (
                        <>
                          <LogIn className="w-3.5 h-3.5" /> Log in to Sahaj Connect Account
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-4 text-xs text-center border rounded-xl space-y-1 bg-muted/20">
                  {tokenInfo.status === 'SCHEDULED' ? (
                    <div className="text-blue-400 font-semibold flex items-center justify-center gap-1.5">
                      <Clock className="w-4 h-4" /> Meeting has not started yet. Starts at {new Date(tokenInfo.startTime).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} ({tokenInfo.timezone}).
                    </div>
                  ) : tokenInfo.status === 'CANCELLED' ? (
                    <div className="text-red-400 font-semibold flex items-center justify-center gap-1.5">
                      <AlertCircle className="w-4 h-4" /> This meeting was cancelled by the host.
                    </div>
                  ) : tokenInfo.status === 'EXPIRED' ? (
                    <div className="text-amber-400 font-semibold flex items-center justify-center gap-1.5">
                      <Clock className="w-4 h-4" /> This meeting link has expired.
                    </div>
                  ) : (
                    <div className="text-muted-foreground font-semibold flex items-center justify-center gap-1.5">
                      <Video className="w-4 h-4" /> This meeting has ended.
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
